import React from 'react';
import ReactDOM from 'react-dom/client';
import { broadcastResponseToMainFrame } from '@azure/msal-browser/redirect-bridge';
import App from './App';
import { installAuthInterceptor } from './lib/apiClient';
// Imported before anything else renders: it catches the browser's one-off
// `beforeinstallprompt`, which fires early and is lost if nobody is listening.
import { UpdateToast } from './lib/pwa';
import { register as registerServiceWorker } from './serviceWorkerRegistration';

/*
 * MICROSOFT'S QUIET SIGN-IN LANDS HERE, IN A HIDDEN FRAME.
 *
 * lib/auth signs a remembered person back in without a press (ssoSilent),
 * and renews tokens the same way: Microsoft is asked in a hidden frame, and
 * sends its answer to the redirect URI - this page - inside that frame.
 * MSAL 5 waits for the page there to hand the answer back over a broadcast
 * channel (its "redirect bridge"). Booting the whole app in the frame did
 * not, so every quiet attempt timed out and the front door asked again.
 *
 * So in a frame that holds an answer from Microsoft (a `state`, with a
 * `code` or an `error`), this page is the bridge and nothing else: no app,
 * no service worker, no second copy of Jarvis running unseen.
 */
const authAnswer = () => {
  try {
    if (window.parent === window) return false;
    const where = `${window.location.hash || ''}&${window.location.search || ''}`;
    return /[#?&]state=/.test(where) && /[#?&](code|error)=/.test(where);
  } catch {
    return false;
  }
};

if (authAnswer()) {
  broadcastResponseToMainFrame().catch(() => {
    // Nothing to hand back (or it could not be read): the app's own wait
    // runs out and it shows the front door - the right answer either way.
  });
} else {
  // Every API call carries the staff token once someone has signed in.
  installAuthInterceptor();

  const root = ReactDOM.createRoot(document.getElementById('root'));
  root.render(
    <React.StrictMode>
      <App />
      {/* "Jarvis has been updated - Reload", shown when a new deploy takes
          over this page. Mounted here, beside App rather than inside it, so
          it outlives every route and App.js stays as it was. */}
      <UpdateToast />
    </React.StrictMode>
  );

  // The installable-app plumbing: production builds only, https or localhost.
  registerServiceWorker();
}
